
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import SEOArticle from "@/components/SEOArticle";
import SEO from '@/components/SEO';
import { 
  generateArticleSchema, 
  generateBreadcrumbSchema, 
  generateMetaKeywords 
} from '@/utils/seoUtils';
import { seoKnowledgeBaseData } from "@/data/seoKnowledgeBase";
import { SEOKnowledgeItem } from "@/types";
import { Button } from "@/components/ui/button";

const KnowledgeArticle = () => {
  const location = useLocation();
  const [article, setArticle] = useState<SEOKnowledgeItem | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<SEOKnowledgeItem[]>([]);

  useEffect(() => {
    const currentPath = location.pathname;
    const articleId = currentPath.split('/').pop();
    
    const foundArticle = seoKnowledgeBaseData.find(item => item.id === articleId);
    
    if (foundArticle) {
      setArticle(foundArticle);
      
      const related = seoKnowledgeBaseData
        .filter(item => item.category === foundArticle.category && item.id !== articleId)
        .slice(0, 3);
      
      setRelatedArticles(related);
    }
  }, [location.pathname]);

  const currentUrl = window.location.origin + location.pathname;

  if (!article) {
    return (
      <PageTransition>
        <SEO 
          seo={{
            title: "Article Not Found | Los Angeles SEO Group",
            description: "The article you're looking for doesn't exist or has been moved.",
            noindex: true
          }}
          url={currentUrl}
        />
        <Navbar />
        <main className="min-h-screen pt-24 pb-16">
          <div className="page-container">
            <div className="text-center py-12 bg-white dark:bg-card rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-2">Article not found</h3>
              <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist or has been moved.</p>
              <Button asChild>
                <Link to="/knowledge-base" className="inline-flex items-center">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Knowledge Base
                </Link>
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </PageTransition>
    );
  }

  // Generate keywords from article content
  const contentText = article.content.map(section => section.content).join(' ');
  const metaKeywords = generateMetaKeywords(contentText, [
    article.categoryLabel, 
    'SEO', 
    'Search Engine Optimization',
    'Los Angeles SEO',
    article.title.split(':')[0]
  ]);

  // Generate breadcrumbs data
  const breadcrumbs = [
    { name: 'Home', url: 'https://losangelesseogroup.com/' },
    { name: 'Knowledge Base', url: 'https://losangelesseogroup.com/knowledge-base/' },
    { name: article.categoryLabel, url: `https://losangelesseogroup.com/knowledge-base?category=${article.category}` },
    { name: article.title, url: currentUrl }
  ];

  return (
    <PageTransition>
      {article && (
        <SEO 
          seo={{
            title: `${article.title} | Los Angeles SEO Group`,
            description: article.excerpt || article.summary,
            ogImage: article.coverImage || '/og-image.png',
            ogType: 'article',
            keywords: metaKeywords,
            author: article.author?.name,
            publishedTime: article.date,
            modifiedTime: article.lastUpdated || article.date,
            jsonLd: {
              article: generateArticleSchema({
                title: article.title,
                description: article.excerpt || article.summary,
                image: article.coverImage || '/og-image.png',
                publishDate: article.date || new Date().toISOString(),
                lastModified: article.lastUpdated || article.date || new Date().toISOString(),
                url: currentUrl,
                author: article.author || { name: 'Los Angeles SEO Group' },
                keywords: metaKeywords
              }),
              breadcrumb: generateBreadcrumbSchema(breadcrumbs)
            }
          }}
          url={currentUrl}
        />
      )}
      
      <Navbar />
      <main className="min-h-screen pt-24 pb-16">
        <section className="section-padding bg-gradient-to-b from-desert-light to-background">
          <div className="page-container">
            <div className="max-w-3xl mx-auto">
              {/* Breadcrumbs for SEO and UX */}
              <div className="text-sm text-muted-foreground mb-4">
                <Link to="/" className="hover:text-primary">Home</Link>
                <span className="mx-1">/</span>
                <Link to="/knowledge-base" className="hover:text-primary">Knowledge Base</Link>
                <span className="mx-1">/</span>
                <span>{article.categoryLabel}</span>
              </div>
            
              <Link to="/knowledge-base" className="inline-flex items-center text-primary hover:underline mb-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Knowledge Base
              </Link>
              
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-desert-sunset/10 text-desert-sunset mb-2">
                {article.categoryLabel}
              </span>
              
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{article.title}</h1>
              <p className="text-lg text-foreground/70 mb-8">{article.summary}</p>
              
              <div className="bg-white dark:bg-card rounded-xl shadow-sm p-6 mb-12">
                <SEOArticle article={article} />
              </div>
              
              {relatedArticles.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
                  <div className="grid gap-4">
                    {relatedArticles.map(relatedArticle => (
                      <motion.div 
                        key={relatedArticle.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Link 
                          to={`/knowledge-base/${relatedArticle.id}`}
                          className="block p-4 rounded-lg border hover:bg-muted/50 transition-colors bg-white dark:bg-card"
                        >
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-desert-sunset/10 text-desert-sunset mb-1">
                            {relatedArticle.categoryLabel}
                          </span>
                          <h3 className="font-semibold mb-1">{relatedArticle.title}</h3>
                          <p className="text-sm text-muted-foreground">{relatedArticle.summary}</p>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </PageTransition>
  );
};

export default KnowledgeArticle;
